/**
 * The player's entire game state. This shape is what lives in IndexedDB, what
 * gets synced to D1 as a JSON blob, and what the pure logic in this folder
 * operates on. No React, no storage concerns.
 */

/** Current shape version. Bump when a migration is needed in `storage/local.ts`. */
export const SCHEMA_VERSION = 1;

/**
 * A set of claimed plates: plateId -> timestamp of the claim that established it.
 *
 * Note this is "when the current claim was made", not strictly "first ever
 * seen" - see `merge.ts`. In the normal case (claim once, never undo) they are
 * the same thing.
 */
export type ClaimSet = Record<string, number>;

/** plateId -> timestamp of the undo. Beats a claim with an earlier timestamp. */
export type Tombstones = Record<string, number>;

export interface LeaderboardEntry {
  /**
   * `publicIdFor(playerId)`, never the player id itself. The player id is the
   * bearer credential for sync and sign-in, so a board carrying it would hand
   * everyone on it the means to take over everyone else.
   */
  publicId: string;
  displayName: string;
  score: number;
  plates: number;
}

export interface TripState {
  id: string;
  joinCode: string;
  name: string;
  startedAt: number;
  endedAt: number | null;
  /**
   * False until the server knows about this trip.
   *
   * A trip can be started with no signal, so it exists locally first and is
   * registered on the next successful sync. Until then its join code is only a
   * proposal - the server may hand back a different one if it has been taken.
   */
  registered: boolean;
  /** True when this player created the trip, rather than joining someone else's. */
  isOwner: boolean;
  /** This player's claims for this trip. Scores start fresh per trip. */
  myClaims: ClaimSet;
  undone: Tombstones;
  /** Last known standings. Refreshed by piggybacking on sync. */
  leaderboard: LeaderboardEntry[];
  leaderboardAt: number;
  version: number;
}

export interface PlayerState {
  schemaVersion: number;
  /** 128-bit random id. Doubles as the bearer credential for /api/sync. */
  playerId: string;
  displayName: string;
  /** The plate-dex. Grows forever; never reset by a trip. */
  lifetime: ClaimSet;
  lifetimeUndone: Tombstones;
  trips: TripState[];
  /** null means lifetime mode, which is the default. */
  activeTripId: string | null;
  /** Monotonic. Bumped on every mutation; drives sync reconciliation. */
  version: number;
}

export function emptyPlayerState(playerId: string, displayName = 'Player'): PlayerState {
  return {
    schemaVersion: SCHEMA_VERSION,
    playerId,
    displayName,
    lifetime: {},
    lifetimeUndone: {},
    trips: [],
    activeTripId: null,
    version: 0,
  };
}

export function activeTrip(state: PlayerState): TripState | null {
  if (!state.activeTripId) return null;
  const trip = state.trips.find((t) => t.id === state.activeTripId);
  if (!trip || trip.endedAt !== null) return null;
  return trip;
}
